import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { PmsCredentialFields } from '../forms/pms-credential-fields.js';
import { useCreatePmsCredential, useUpdatePmsCredential } from '../api/mutations.js';
import { usePmsCredential } from '../api/queries.js';
import {
  PMS_CREDENTIAL_DEFAULTS,
  type PmsCredentialDetail,
  type PmsCredentialFormInput,
  pmsCredentialFormInput,
} from '../api/schemas.js';

/** 409. Named because the linter forbids a bare numeric literal here. */
const HTTP_CONFLICT = 409;

const CREATE_DEFAULTS: PmsCredentialFormInput = PMS_CREDENTIAL_DEFAULTS;

function stringOfNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function stringOfBoolean(value: boolean | undefined): 'true' | 'false' {
  return value === true ? 'true' : 'false';
}

function defaultsFrom(credential: PmsCredentialDetail): PmsCredentialFormInput {
  return {
    name: credential.name,
    pmsProvider: credential.pmsProvider,
    travelAgentId: stringOfNumber(credential.travelAgentId),
    marketSegmentId: stringOfNumber(credential.marketSegmentId),
    bookingSourceId: stringOfNumber(credential.bookingSourceId),
    partialReservationExpireTime: stringOfNumber(credential.partialReservationExpireTime),
    useTestEnvironment: stringOfBoolean(credential.useTestEnvironment),
    status: credential.status,
    baseUrl: credential.baseUrl ?? '',
    agentId: stringOfNumber(credential.agentId),
    agentSecret: credential.agentSecret,
    clientId: stringOfNumber(credential.clientId),
    clientSecret: credential.clientSecret,
    rateGridAgentId: stringOfNumber(credential.rateGridAgentId),
    timezone: credential.timezone ?? '',
    masterRateTravelAgentId: stringOfNumber(credential.masterRateTravelAgentId),
    reservationTravelAgentId: stringOfNumber(credential.reservationTravelAgentId),
    moduleType: credential.moduleType.join(', '),
  };
}

export function PmsCredentialFormPage(): React.JSX.Element {
  const pmsCredentialId = useParams().pmsCredentialId;
  const isEdit = pmsCredentialId !== undefined;

  return (
    <PermissionGuard permission={PERMISSIONS.PMS_CONFIGURE} fallback={<NotPermitted />}>
      {isEdit ? <EditPmsCredential pmsCredentialId={pmsCredentialId} /> : <CreatePmsCredential />}
    </PermissionGuard>
  );
}

function CreatePmsCredential(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreatePmsCredential();

  const form = useAppForm<PmsCredentialFormInput>({
    schema: pmsCredentialFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'PMS credential created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate(`/pms-credentials/${created.id}`);
    },
  });

  return (
    <FormPage
      title="New PMS credential"
      description="Create a connection to a property management system."
      backTo="/pms-credentials"
      backLabel="PMS credentials"
      submitLabel="Create credential"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PmsCredentialFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}

function EditPmsCredential({
  pmsCredentialId,
}: {
  readonly pmsCredentialId: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdatePmsCredential();
  const query = usePmsCredential(pmsCredentialId);

  if (query.isPending) return <LoadingState label="Loading PMS credential" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditPmsCredentialForm
      credential={query.data}
      onSaved={() => {
        void navigate(`/pms-credentials/${pmsCredentialId}`);
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({
            id: pmsCredentialId,
            version: query.data.version,
            values,
          });
        } catch (error) {
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await query.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditPmsCredentialForm({
  credential,
  save,
  onSaved,
}: {
  readonly credential: PmsCredentialDetail;
  readonly save: (values: PmsCredentialFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<PmsCredentialFormInput>({
    schema: pmsCredentialFormInput,
    defaultValues: defaultsFrom(credential),
    successMessage: 'PMS credential updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={`Edit ${credential.name}`}
      description="Changes take effect immediately for every property using this connection."
      backTo="/pms-credentials"
      backLabel="PMS credentials"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <PmsCredentialFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}
